import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './useAuth';
import { ROUTES } from '@shared/constants/routes';
import { Role } from '../types/auth.types';

interface UseGuestGuardOptions {
  redirectTo?: string;
}

/**
 * Hook that redirects authenticated users away from guest-only pages (login, register, etc.)
 * Redirects admins/vendors to their dashboard, regular users to home
 */
export function useGuestGuard(options: UseGuestGuardOptions = {}) {
  const { redirectTo } = options;
  const { user, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (isLoading) return;

    if (isAuthenticated && user) {
      // Determine redirect destination based on user role
      let destination = redirectTo;

      if (!destination) {
        // Admins go to admin dashboard, vendors to vendor dashboard, customers to home
        if (user.role === Role.ADMIN) {
          destination = ROUTES.ADMIN.DASHBOARD;
        } else if (user.role === Role.VENDOR) {
          destination = ROUTES.VENDOR.DASHBOARD;
        } else {
          destination = ROUTES.HOME;
        }
      }

      navigate(destination, { replace: true });
    }
  }, [isAuthenticated, isLoading, user, navigate, redirectTo]);

  return {
    isGuest: !isAuthenticated,
    isLoading,
  };
}
